import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { quantAssetApplicability, getErrorMessage } from '../../lib/api';

const STATUS_CONFIG = {
  applicable: {
    icon: CheckCircle2,
    label: 'Applicabile',
    description: 'L\'asset è incluso esplicitamente nell\'universe della strategia: puoi vedere già il backtest.',
    tone: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500/30',
    badgeTone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    icoTone: 'text-emerald-600',
  },
  recommended: {
    icon: CheckCircle2,
    label: 'Consigliata',
    description: 'La classe di asset è compatibile, ma il ticker non è nell\'universe esistente. Per testarla qui, crea una variante.',
    tone: 'bg-sky-50 dark:bg-sky-950/30 border-sky-500/30',
    badgeTone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    icoTone: 'text-sky-600',
  },
  avoid: {
    icon: AlertTriangle,
    label: 'Sconsigliata',
    description: 'La classe di asset è esplicitamente nella lista da evitare per ragioni strutturali.',
    tone: 'bg-amber-50 dark:bg-amber-950/30 border-amber-500/30',
    badgeTone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    icoTone: 'text-amber-600',
  },
  unsupported: {
    icon: HelpCircle,
    label: 'Non testata',
    description: 'La compatibilità è ignota: la classe non è dichiarata né come consigliata né come da evitare.',
    tone: 'bg-muted/30 border-muted',
    badgeTone: 'bg-muted text-muted-foreground',
    icoTone: 'text-muted-foreground',
  },
};

/**
 * Card che mostra le strategie applicabili ad un asset, classificate.
 *
 * Props:
 *  - symbol: ticker
 *  - onApplyStrategy: (name) => void — apre la strategia in tab Grafico
 *  - onProposeEvolution: (name) => void — apre PromptBuilder con propose-evolution
 */
const StrategyApplicability = ({ symbol, onApplyStrategy, onProposeEvolution }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    quantAssetApplicability(symbol)
      .then(r => setData(r.data))
      .catch(e => {
        if (e.response?.status !== 404) {
          toast.error(getErrorMessage(e, 'Errore caricamento applicabilità'));
        }
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) {
    return <div className="h-24 bg-muted animate-pulse rounded-lg" />;
  }
  if (!data || !data.items?.length) {
    return null;
  }

  // Raggruppo per status per UX più leggibile
  const groups = {
    applicable: data.items.filter(i => i.status === 'applicable'),
    recommended: data.items.filter(i => i.status === 'recommended'),
    avoid: data.items.filter(i => i.status === 'avoid'),
    unsupported: data.items.filter(i => i.status === 'unsupported'),
  };
  const order = ['applicable', 'recommended', 'avoid', 'unsupported'];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Strategie su {symbol}
          <span className="text-xs text-muted-foreground font-normal">
            {data.items.length} valutate · asset class:{' '}
            <span className="font-mono">{data.asset?.asset_class}</span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {order.filter(s => groups[s].length).map(status => {
          const cfg = STATUS_CONFIG[status];
          const Ico = cfg.icon;
          return (
            <div key={status}>
              <p className="text-xs font-medium flex items-center gap-1.5 mb-1.5">
                <Ico className={`w-3.5 h-3.5 ${cfg.icoTone}`} />
                <span className={cfg.icoTone}>{cfg.label}</span>
                <Badge variant="outline" className="text-[9px]">{groups[status].length}</Badge>
              </p>
              <p className="text-[11px] text-muted-foreground mb-2 ml-5 italic">{cfg.description}</p>
              <div className="space-y-1.5 ml-5">
                {groups[status].map(it => (
                  <div key={it.name} className={`rounded-md border px-3 py-2 ${cfg.tone}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                          <span className="font-mono">{it.name}</span>
                          <Badge variant="outline" className="text-[9px]">{it.type}</Badge>
                          {it.horizon && (
                            <Badge variant="outline" className="text-[9px]">{it.horizon}</Badge>
                          )}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                          {it.reason}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {status === 'applicable' && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => onApplyStrategy?.(it.name)}
                            className="h-7 text-xs"
                          >
                            <Play className="w-3 h-3 mr-1" /> Apri
                          </Button>
                        )}
                        {(status === 'recommended' || status === 'unsupported') && onProposeEvolution && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onProposeEvolution(it.name)}
                            className="h-7 text-xs"
                          >
                            <Sparkles className="w-3 h-3 mr-1" /> Variante
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default StrategyApplicability;
